import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, IconButton,
  CircularProgress, Divider, Tooltip, Alert,
} from '@mui/material';
import CloseIcon      from '@mui/icons-material/Close';
import LinkIcon       from '@mui/icons-material/Link';
import QrCodeIcon     from '@mui/icons-material/QrCode';
import EmailIcon      from '@mui/icons-material/Email';
import CheckIcon      from '@mui/icons-material/Check';
import ShareIcon      from '@mui/icons-material/Share';
import { useAuth }    from '../../../contexts/AuthContext';
import { createApplabShare } from '../../pitch/services/pitchService';
import { APPLAB_ACCENT }     from '../constants';
import QRDialog              from '../../shared/sharing/QRDialog';
import AppLabMailDialog      from './AppLabMailDialog';

export default function ShareModal({ app, open, onClose }) {
  const { currentUser } = useAuth();

  const [title,     setTitle]     = useState('');
  const [desc,      setDesc]      = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [busy,      setBusy]      = useState(false);
  const [error,     setError]     = useState('');
  const [share,     setShare]     = useState(null); // { pitchId, shareToken, url }
  const [linkCopied, setLinkCopied] = useState(false);
  const [qrOpen,    setQrOpen]    = useState(false);
  const [mailOpen,  setMailOpen]  = useState(false);

  function handleOpen() {
    setTitle(app?.name || '');
    setDesc(app?.tagline || '');
    setExpiresAt('');
    setShare(null);
    setError('');
    setLinkCopied(false);
  }

  async function createShare() {
    if (!app || !currentUser) return;
    setBusy(true);
    setError('');
    try {
      const expiresDate = expiresAt ? new Date(expiresAt) : null;
      const { pitchId, shareToken } = await createApplabShare({
        appId:            app.id,
        appName:          title.trim() || app.name,
        tagline:          desc.trim(),
        createdBy:        currentUser.uid,
        ownerDisplayName: currentUser.displayName || '',
        expiresAt:        expiresDate,
      });
      setShare({
        pitchId,
        shareToken,
        url: `${window.location.origin}/p/${shareToken}`,
      });
    } catch (e) {
      setError(e.message || 'Misslyckades att skapa delning');
    } finally {
      setBusy(false);
    }
  }

  function copyLink() {
    if (!share?.url) return;
    navigator.clipboard.writeText(share.url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      color: 'white', bgcolor: 'rgba(255,255,255,0.04)',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
      '&:hover fieldset': { borderColor: APPLAB_ACCENT },
      '&.Mui-focused fieldset': { borderColor: APPLAB_ACCENT },
    },
    '& label': { color: '#888' },
    '& label.Mui-focused': { color: APPLAB_ACCENT },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        TransitionProps={{ onEnter: handleOpen }}
        PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <ShareIcon sx={{ color: APPLAB_ACCENT, fontSize: 20 }} />
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={700}>Dela app</Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              Skapar en publik länk för externa mottagare
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: '#666', '&:hover': { color: 'white' } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {!share ? (
            <>
              <TextField
                label="Titel" size="small" fullWidth
                value={title} onChange={e => setTitle(e.target.value)}
                sx={fieldSx}
              />
              <TextField
                label="Beskrivning (valfritt)" size="small" fullWidth multiline rows={2}
                value={desc} onChange={e => setDesc(e.target.value)}
                sx={fieldSx}
              />
              <TextField
                label="Utgångsdatum (valfritt)" size="small" fullWidth
                type="date" InputLabelProps={{ shrink: true }}
                value={expiresAt} onChange={e => setExpiresAt(e.target.value)}
                sx={fieldSx}
              />

              <Box sx={{
                bgcolor: 'rgba(0,137,123,0.08)', border: '1px solid rgba(0,137,123,0.2)',
                borderRadius: 1.5, px: 1.5, py: 1, fontSize: '0.78rem', color: '#aaa',
              }}>
                Externa mottagare kan använda appens lokala funktioner men AI-anrop är inaktiverade.
                Ingen AppLAB-branding syns för mottagaren.
              </Box>

              {error && <Alert severity="error" sx={{ fontSize: '0.8rem' }}>{error}</Alert>}
            </>
          ) : (
            <>
              <Box sx={{
                bgcolor: 'rgba(0,137,123,0.08)', border: '1px solid rgba(0,137,123,0.25)',
                borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5,
              }}>
                <Typography fontWeight={700} sx={{ color: APPLAB_ACCENT, fontSize: '0.88rem' }}>
                  Delning skapad
                </Typography>
                <Box sx={{
                  bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 1, px: 1.5, py: 1, fontFamily: 'monospace', fontSize: '0.78rem',
                  color: '#ccc', wordBreak: 'break-all',
                }}>
                  {share.url}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Tooltip title={linkCopied ? 'Kopierat!' : 'Kopiera länk'}>
                    <Button
                      size="small" variant="contained"
                      startIcon={linkCopied ? <CheckIcon sx={{ fontSize: 15 }} /> : <LinkIcon sx={{ fontSize: 15 }} />}
                      onClick={copyLink}
                      sx={{
                        textTransform: 'none', fontSize: '0.8rem',
                        bgcolor: linkCopied ? '#2e7d32' : APPLAB_ACCENT,
                        '&:hover': { bgcolor: linkCopied ? '#1b5e20' : '#00695C' },
                      }}
                    >
                      {linkCopied ? 'Kopierad!' : 'Kopiera länk'}
                    </Button>
                  </Tooltip>
                  <Button
                    size="small" variant="outlined"
                    startIcon={<QrCodeIcon sx={{ fontSize: 15 }} />}
                    onClick={() => setQrOpen(true)}
                    sx={{ textTransform: 'none', fontSize: '0.8rem', borderColor: 'rgba(255,255,255,0.2)', color: '#ccc', '&:hover': { borderColor: APPLAB_ACCENT, color: 'white' } }}
                  >
                    QR-kod
                  </Button>
                  <Button
                    size="small" variant="outlined"
                    startIcon={<EmailIcon sx={{ fontSize: 15 }} />}
                    onClick={() => setMailOpen(true)}
                    sx={{ textTransform: 'none', fontSize: '0.8rem', borderColor: 'rgba(255,255,255,0.2)', color: '#ccc', '&:hover': { borderColor: APPLAB_ACCENT, color: 'white' } }}
                  >
                    Skicka mejl
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none', color: '#888' }}>Stäng</Button>
          {!share && (
            <Button
              variant="contained" onClick={createShare} disabled={busy || !title.trim()}
              startIcon={busy ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <ShareIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', bgcolor: APPLAB_ACCENT, '&:hover': { bgcolor: '#00695C' } }}
            >
              {busy ? 'Skapar…' : 'Skapa delning'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {share && (
        <>
          <QRDialog
            open={qrOpen}
            onClose={() => setQrOpen(false)}
            url={share.url}
            title={title || app?.name}
          />
          <AppLabMailDialog
            open={mailOpen}
            onClose={() => setMailOpen(false)}
            shareUrl={share.url}
            appName={title || app?.name || ''}
            description={desc}
          />
        </>
      )}
    </>
  );
}
